import { AlertTriangle } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { z } from "zod";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaPasswordField } from "@/shared/components/forms/ColocaPasswordField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { ColocaDialog, ColocaDialogClose } from "@/shared/components/overlays/ColocaDialog";
import { useSecurityPin } from "@/shared/hooks/useSecurityPin";
import { useTranslations } from "@/shared/lang/useTranslations";
import { colocaToast } from "@/shared/utils/toast";

interface RemoveSecurityPinDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Removing the PIN leaves the account with no step-up method, so the dialog says what stops working and asks
 * for the password — the same proof that created it.
 */
export function RemoveSecurityPinDialog({ open, onOpenChange }: RemoveSecurityPinDialogProps) {
  return open ? <RemoveSecurityPinDialogContent onOpenChange={onOpenChange} /> : null;
}

function RemoveSecurityPinDialogContent({
  onOpenChange,
}: Pick<RemoveSecurityPinDialogProps, "onOpenChange">) {
  const { t } = useTranslations("settings");
  const { t: tg } = useTranslations("global");
  const { remove, saving } = useSecurityPin();
  const formId = useId();
  const [formError, setFormError] = useState<string | null>(null);

  const schema = useMemo(
    () => z.object({ password: z.string().min(1, tg("security_pin_password_required")) }),
    [tg],
  );
  const form = useZodForm(schema, { password: "" });

  const submit = async ({ password }: { password: string }) => {
    setFormError(null);
    const failure = await remove(password);
    if (!failure) {
      colocaToast.success(t("security_pin_removed_toast"));
      onOpenChange(false);
      return;
    }
    if (failure.field === "password") {
      form.setError("password", { message: failure.message }, { shouldFocus: true });
      return;
    }
    setFormError(failure.message);
  };

  return (
    <ColocaDialog
      open
      onOpenChange={(next) => {
        if (!next && saving) return;
        onOpenChange(next);
      }}
      showCloseButton={!saving}
      size="sm"
      title={t("security_pin_remove_title")}
      footer={
        <>
          <ColocaDialogClose render={<ColocaButton variant="ghost" disabled={saving} />}>
            {t("security_pin_remove_cancel")}
          </ColocaDialogClose>
          <ColocaButton variant="destructive" type="submit" form={formId} loading={saving}>
            {t("security_pin_remove_confirm")}
          </ColocaButton>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <ColocaAlert variant="warning" icon={AlertTriangle} text={t("security_pin_remove_body")} />
        {formError ? <ColocaAlert variant="destructive" icon={AlertTriangle} text={formError} /> : null}
        <ColocaForm form={form} onSubmit={submit} id={formId} className="space-y-0">
          <ColocaPasswordField
            name="password"
            label={tg("security_pin_password_label")}
            autoComplete="current-password"
            showLabel={tg("security_pin_password_show")}
            hideLabel={tg("security_pin_password_hide")}
          />
        </ColocaForm>
      </div>
    </ColocaDialog>
  );
}
